/** One error vocabulary for both adapters, so callers never branch on transport details. */
export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = "Not signed in") {
    super(message, 401);
    this.name = "UnauthorizedError";
  }
}

export class ForbiddenError extends ApiError {
  constructor(message = "Not permitted") {
    super(message, 403);
    this.name = "ForbiddenError";
  }
}

export class NotFoundError extends ApiError {
  constructor(message = "Not found") {
    super(message, 404);
    this.name = "NotFoundError";
  }
}

/** A 409 whose refusal is about a NAME colliding, not a state machine — `profiles.update`'s
 * `"That profile URL is already taken."`. Its own class so a form can show the message next to
 * the slug field rather than as a generic toast. */
export class ConflictError extends ApiError {
  constructor(message = "That value is already taken") {
    super(message, 409);
    this.name = "ConflictError";
  }
}

/**
 * W9 §E-7 — `documents.merge.commit` answers **409 `{error, unresolved}`**, and that count is
 * delivered nowhere else. kms read `result.unresolved` off a 200 the controller never sends,
 * so its "N parts still need a decision" toast could never fire; this is the typed refusal that
 * lets slate's actually fire.
 */
export class MergeUnresolvedError extends ApiError {
  readonly unresolved: number;

  constructor(unresolved: number, message = "Unresolved parts remain.") {
    super(message, 409);
    this.name = "MergeUnresolvedError";
    this.unresolved = unresolved;
  }
}

/**
 * A sculpt refusal with its wire code intact. The one worth branching on is
 * `apply_lane_unsupported`: the document's live state is in a collab room the server cannot
 * write for this lane, and the caller resolves it by asking for the propose lane instead.
 */
export class SculptError extends ApiError {
  readonly code: string;

  constructor(code: string, message: string, status: number) {
    super(message, status);
    this.name = "SculptError";
    this.code = code;
  }
}

/** The workspace or estate kill switch is off — every sculpt affordance should simply hide. */
export class SculptDisabledError extends ForbiddenError {
  constructor(message = "AI sculpting is turned off for this workspace") {
    super(message);
    this.name = "SculptDisabledError";
  }
}

/**
 * A destructive act-lane sculpt needs an explicit confirmation. The token is a drift
 * fingerprint of the document's current text: re-post with it to proceed, and any edit in
 * between mints a different token so a stale confirmation can never clobber newer content.
 */
export class SculptConfirmRequiredError extends ApiError {
  readonly confirmToken: string;
  readonly partsReplaced: number;

  constructor(confirmToken: string, partsReplaced: number, message = "Confirmation required") {
    super(message, 409);
    this.name = "SculptConfirmRequiredError";
    this.confirmToken = confirmToken;
    this.partsReplaced = partsReplaced;
  }
}

/**
 * A typed refusal from a W2 run-control endpoint (jump/publish/step/breakpoints/…). The wire
 * `code` is kept intact — `unknown_node`, `run_is_live`, `no_destination`, `bad_destination`,
 * `stepping_unsupported`, `unsupported_for_kind` — so a caller can branch on it exactly as it
 * would on `SculptError.code`, rather than string-matching a translated message. FakeApi throws
 * this for every typed run-control refusal; the real adapter's generic transport does not yet
 * parse structured error bodies for these routes (only the message survives) — a follow-up for
 * whoever wires the run viewer against a live backend.
 */
export class AutomationError extends ApiError {
  readonly code: string;
  readonly knownNodes?: string[];

  constructor(code: string, message: string, status = 400, knownNodes?: string[]) {
    super(message, status);
    this.name = "AutomationError";
    this.code = code;
    this.knownNodes = knownNodes;
  }
}

/**
 * `ai-flows.save` refused the definition (`400 {error:"invalid_flow", errors:[…]}`).
 *
 * It is its own class because the REASONS are the whole point. The studio already runs the
 * package's `diagnostics()` before offering Save, so the rules that reach here are precisely the
 * ones only the server checks — the `output` block, unknown models, the `work`/`human` kind
 * rules, whether a subflow's slug exists. Collapsing that list into `ApiError`'s single message
 * would leave the author looking at a valid-looking canvas and an unexplained refusal.
 */
export class FlowValidationError extends ApiError {
  readonly errors: string[];
  readonly warnings: string[];

  constructor(message: string, errors: string[], warnings: string[] = [], status = 400) {
    super(message, status);
    this.name = "FlowValidationError";
    this.errors = errors;
    this.warnings = warnings;
  }
}

export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}
